import { Injectable, inject } from '@angular/core';
import {
  Agent,
  AGENT_PROJECTION_SIGNAL_IDS,
  ElasticSearchAgent,
  DomainSignalId,
  PlatformAgentEmailGroupsResponse,
  PlatformAgentListResponse,
  PlatformAgentRow,
  PlatformAgentSearchResponse,
  PlatformAgentsByEmailResponse,
} from '@ag-common-lib/public-api';
import { LoadOptions } from 'devextreme/data';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { merge } from 'lodash';
import { DomainSignalsService } from '../domain-signals.service';
import { ReportingApiService } from '../reporting-api.service';
import {
  AgentEmailAddressGroupsResult,
  AgentExportData,
  AgentSearchResult,
  AgentSearchService,
  AgentSearchStoreOptions,
} from './agent-search.service';

const AGENTS_GRID_PAGE_SIZE = 50;

/**
 * The most specific message an API failure carries.
 *
 * Angular wraps a non-2xx as an `HttpErrorResponse` whose `error` holds the parsed body,
 * so the server's own message — the useful one — is nested one level in. Its own
 * `message` is the generic "Http failure response for …", used only as a fallback.
 */
function readApiErrorMessage(error: unknown): string {
  const body = (error as { error?: unknown })?.error;
  const nested = typeof body === 'string' ? body : (body as { message?: string; error?: string })?.message;

  return (
    nested ||
    (body as { error?: string })?.error ||
    (error as { message?: string })?.message ||
    'The agent service could not be reached.'
  );
}

/**
 * Agents, answered from the `platform.agents` projection over the reporting API.
 *
 * The rows arrive keyed by `AgentKeys`, so nothing here reshapes them — the server
 * composes `p_agent_name` and resolves the agency names that the Elasticsearch service
 * had to add to every row after the fact.
 */
@Injectable({ providedIn: 'root' })
export class AgentSqlSearchService extends AgentSearchService {
  private readonly reportingApiService = inject(ReportingApiService);
  private readonly domainSignals = inject(DomainSignalsService);

  private readonly basePath = '/api/platform-agents';

  /**
   * Every call to the API, with failures turned into something a person can read.
   *
   * DevExtreme renders a rejected store load by stringifying it, so a rejection that is
   * a plain object — which is what an HTTP error body is — reaches the grid as the
   * literal text `[object Object]`, while the server's actual message sits inside it
   * unread. That is not a hypothetical: a renamed route produced exactly that banner,
   * with `Route POST:/api/platform-agents/get-list not found` in the payload.
   *
   * An `Error` carries its text in `message`, which is what the grid shows. This matters
   * most for the export, where the server refuses a filter it cannot honour and the
   * reason ("unknown or unfilterable field …") is the entire value of the refusal.
   */
  private async request<T>(path: string, body: unknown): Promise<T> {
    try {
      return await this.reportingApiService.request<T>(`${this.basePath}${path}`, { method: 'POST', body });
    } catch (error) {
      throw new Error(readApiErrorMessage(error));
    }
  }

  /**
   * Resolved agents, keyed by dbId. A name lookup used from cell templates: grids call it
   * once per rendered owner cell and again whenever the template repaints, and every miss
   * would otherwise be its own request. The promise, not the value, is cached so
   * concurrent callers for one id share a request; a rejection is evicted so a transient
   * failure stays retryable.
   *
   * Unlike the Elasticsearch service's equivalent, this cache is not stuck with a stale
   * name for the rest of the session: the projection publishes a signal naming the agents
   * that changed, and `invalidate` drops exactly those entries.
   */
  private readonly agentByDbIdCache = new Map<string, Promise<Agent>>();

  constructor() {
    super();

    for (const signalId of AGENT_PROJECTION_SIGNAL_IDS) {
      this.domainSignals
        .onChangedRows(signalId)
        .subscribe(change => this.invalidate(change.changedRowKeys, change.isCoarse));
    }

    // An agency rename changes the resolved names on rows that carry no agent change of
    // their own, so its keys — agency ids — never match a cached agent. Always coarse.
    this.domainSignals.onChangedRows(DomainSignalId.agencies).subscribe(() => this.invalidate([], true));
  }

  async search(loadOptions: LoadOptions): Promise<AgentSearchResult> {
    const response = await this.request<PlatformAgentSearchResponse>('/get-list', { loadOptions: loadOptions ?? {} });

    return {
      data: this.toAgents(response?.data),
      totalCount: response?.totalCount,
      groupCount: response?.groupCount,
      summary: response?.summary,
    };
  }

  getById(dbId: string): Promise<Agent> {
    if (!dbId) {
      return Promise.resolve(null);
    }

    const cached = this.agentByDbIdCache.get(dbId);
    if (cached) {
      return cached;
    }

    const request = this.getByIds([dbId])
      .then(agents => agents?.[0] ?? null)
      .catch(error => {
        this.agentByDbIdCache.delete(dbId);
        throw error;
      });

    this.agentByDbIdCache.set(dbId, request);

    return request;
  }

  async getSelf(): Promise<Agent> {
    // No arguments on purpose: the agent id comes from the token server-side, so there is
    // nothing here that could be pointed at another agent.
    const response = await this.request<PlatformAgentListResponse>('/self', {});

    return this.toAgents(response?.data)?.[0] ?? null;
  }

  async getByIds(dbIds: string[]): Promise<Agent[]> {
    const ids = (dbIds ?? []).filter(Boolean);
    if (!ids.length) {
      return [];
    }

    const response = await this.request<PlatformAgentListResponse>('/by-ids', { dbIds: ids });

    return this.toAgents(response?.data);
  }

  async getByEmail(email: string): Promise<Agent[]> {
    const byEmail = await this.getByEmails([email]);

    return byEmail.get(email) ?? [];
  }

  async getByEmails(emails: string[]): Promise<Map<string, Agent[]>> {
    const addresses = (emails ?? []).filter(Boolean);
    const byEmail = new Map<string, Agent[]>();

    if (!addresses.length) {
      return byEmail;
    }

    const response = await this.request<PlatformAgentsByEmailResponse>('/by-email', { emails: addresses });

    for (const group of response?.data ?? []) {
      byEmail.set(group.email, this.toAgents(group.agents));
    }

    return byEmail;
  }

  async getByAgentId(agentId: string): Promise<Agent> {
    const agents = await this.getAgentsByAgentId(agentId);

    return agents?.[0];
  }

  /**
   * At most one agent. The projection holds a unique business id and NULLs the value on
   * whichever Firestore document loses a collision, so the duplicates the Elasticsearch
   * service returned here — and whose oldest its callers took — are resolved upstream now.
   */
  async getAgentsByAgentId(agentId: string): Promise<Agent[]> {
    if (!agentId) {
      return [];
    }

    const response = await this.request<PlatformAgentListResponse>('/by-agent-id', { agentId: String(agentId) });

    return this.toAgents(response?.data);
  }

  async getTeamByAgentId(agentId: string): Promise<Agent[]> {
    if (!agentId) {
      return [];
    }

    const response = await this.request<PlatformAgentListResponse>('/team', { agentId: String(agentId) });

    return this.toAgents(response?.data);
  }

  getDataSource(options?: AgentSearchStoreOptions): DataSource {
    return new DataSource({
      paginate: true,
      pageSize: AGENTS_GRID_PAGE_SIZE,
      store: this.getStore(options),
    });
  }

  getStore(options?: AgentSearchStoreOptions): CustomStore {
    return new CustomStore({
      key: 'dbId',
      byKey: dbId => this.getById(dbId),
      load: async loadOptions => {
        const response = await this.search(merge({}, options, loadOptions));

        return options?.postProcess ? options.postProcess(response) : response;
      },
    });
  }

  async getExportData(loadOptions: LoadOptions): Promise<AgentExportData> {
    const response = await this.request<{ data: PlatformAgentRow[]; meta: AgentExportData['meta'] }>(
      '/get-list/all',
      { loadOptions: loadOptions ?? {} },
    );

    return {
      data: this.toAgents(response?.data),
      meta: response?.meta ?? { totalCount: 0, emailsMax: 0, phoneNumbersMax: 0, addressesMax: 0 },
    };
  }

  /**
   * The map's agents. `userData` carries what the map decides — load-all for the pin
   * layer, a selected location to sort around — while paging and the typed search come
   * through the ordinary load options, so the caller keeps sending exactly what it did.
   */
  async getAgentsGeoPoints(loadOptions: LoadOptions): Promise<ElasticSearchAgent[]> {
    const userData = (loadOptions?.userData ?? {}) as { isLoadingAll?: boolean; geoPoint?: { lat: number; lon: number } | null };
    const response = await this.request<PlatformAgentListResponse>('/geo-points', {
      skip: loadOptions?.skip,
      take: loadOptions?.take,
      isLoadingAll: Boolean(userData.isLoadingAll),
      geoPoint: userData.geoPoint ?? null,
      searchValue: typeof loadOptions?.searchValue === 'string' ? loadOptions.searchValue : null,
    });

    return this.toAgents(response?.data);
  }

  async getEmailAddressGroups(loadOptions: LoadOptions): Promise<AgentEmailAddressGroupsResult> {
    const response = await this.request<PlatformAgentEmailGroupsResponse>('/email-groups', {
      loadOptions: loadOptions ?? {},
    });

    return {
      data: (response?.data ?? []).map(group => ({
        email_addresses: group.email_addresses,
        agentsCount: group.agentsCount,
        agents: this.toAgents(group.agents),
      })),
      totalCount: response?.totalCount ?? 0,
    };
  }

  /**
   * The rows already carry the `AgentKeys` an `Agent` is made of; this only widens the
   * declared type. Nothing is recomputed or renamed, which is the point of the contract.
   */
  private toAgents(rows: PlatformAgentRow[] | undefined | null): ElasticSearchAgent[] {
    return (rows ?? []) as unknown as ElasticSearchAgent[];
  }

  /**
   * Drops what a change made stale.
   *
   * A coarse signal is one whose keys cannot account for the whole version jump — an
   * offline client, or a backfill that moved more rows than one batch can name — so the
   * only safe reading is that anything may have changed.
   */
  private invalidate(changedRowKeys: string[], isCoarse: boolean): void {
    if (isCoarse) {
      this.agentByDbIdCache.clear();
      return;
    }

    for (const dbId of changedRowKeys) {
      this.agentByDbIdCache.delete(dbId);
    }
  }
}
